import { FC, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled from "styled-components";
import {
  CHART_COLORS,
  FRENCH_MONTHS,
  USER_ACTIVITY_THRESHOLDS,
} from "../common/constants";
import { ChartDataPoint, UsersByMonth, UsersChartProps } from "../common/types";

const ChartContainer = styled.div`
  width: 100%;
  margin-bottom: 2rem;
`;

const ChartTitle = styled.h3`
  text-align: center;
  margin-bottom: 1rem;
  color: #333;
`;

const ChartExplanation = styled.div`
  background: #f8f9fa;
  border-left: 4px solid #007bff;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 0 4px 4px 0;
  font-size: 0.9rem;
  color: #495057;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #333;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.5rem;
`;

const StatExplanation = styled.div`
  font-size: 0.8rem;
  color: #888;
  margin-top: 0.5rem;
  font-style: italic;
`;

export const EnhancedUsersChart: FC<UsersChartProps> = ({ users }) => {
  // Calcul des statistiques globales
  const globalStats = useMemo(() => {
    const now = new Date();
    const activeThreshold = new Date(
      now.getTime() - USER_ACTIVITY_THRESHOLDS.ACTIVE_DAYS * 24 * 60 * 60 * 1000
    );
    const inactiveThreshold = new Date(
      now.getTime() -
        USER_ACTIVITY_THRESHOLDS.INACTIVE_DAYS * 24 * 60 * 60 * 1000
    );

    const totalUsers = users.length;
    const activeUsers = users.filter(
      (user) => new Date(user.loginAt || user.updatedAt) > activeThreshold
    ).length;
    const inactiveUsers = users.filter(
      (user) => new Date(user.loginAt || user.updatedAt) < inactiveThreshold
    ).length;
    const retentionRate = totalUsers > 0 ? (activeUsers / totalUsers) * 100 : 0;

    return {
      totalUsers,
      activeUsers,
      inactiveUsers,
      retentionRate: Math.round(retentionRate * 100) / 100,
    };
  }, [users]);

  // Groupement des données par mois avec logique améliorée
  const usersByMonth: UsersByMonth = useMemo(() => {
    const now = new Date();
    const activeThreshold = new Date(
      now.getTime() - USER_ACTIVITY_THRESHOLDS.ACTIVE_DAYS * 24 * 60 * 60 * 1000
    );

    return users.reduce((acc, user) => {
      const createdAtMonth = new Date(user.createdAt).toLocaleString("fr-FR", {
        month: "long",
        year: "numeric",
      });

      const lastActivity = new Date(user.loginAt || user.updatedAt);
      const isActive = lastActivity > activeThreshold;

      if (!acc[createdAtMonth]) {
        acc[createdAtMonth] = {
          new_users: 0,
          inactive_users: 0,
          active_users: 0,
          total_users: 0,
          retention_rate: 0,
          games_played: 0,
        };
      }

      acc[createdAtMonth].new_users += 1;
      acc[createdAtMonth].total_users += 1;

      if (isActive) {
        acc[createdAtMonth].active_users += 1;
      } else {
        acc[createdAtMonth].inactive_users += 1;
      }

      return acc;
    }, {} as UsersByMonth);
  }, [users]);

  // Calcul du taux de rétention par mois
  Object.keys(usersByMonth).forEach((month) => {
    const data = usersByMonth[month];
    data.retention_rate =
      data.total_users > 0
        ? Math.round((data.active_users / data.total_users) * 100 * 100) / 100
        : 0;
  });

  // Conversion en tableau et tri
  const chartData: ChartDataPoint[] = Object.entries(usersByMonth)
    .map(
      ([month, data]: [
        string,
        {
          new_users: number;
          inactive_users: number;
          active_users: number;
          total_users: number;
          retention_rate: number;
          games_played: number;
        }
      ]) => ({
        month,
        ...data,
      })
    )
    .sort((a, b) => {
      const aMonth = a.month.split(" ")[0];
      const bMonth = b.month.split(" ")[0];
      const aYear = a.month.split(" ")[1];
      const bYear = b.month.split(" ")[1];
      if (aYear !== bYear) {
        return aYear.localeCompare(bYear);
      } else {
        return (
          FRENCH_MONTHS.indexOf(aMonth as any) -
          FRENCH_MONTHS.indexOf(bMonth as any)
        );
      }
    });

  return (
    <div>
      {/* Statistiques globales */}
      <ChartExplanation>
        <strong>📊 Statistiques Globales :</strong> Vue d'ensemble de la
        communauté Enefel en temps réel.
      </ChartExplanation>
      <StatsContainer>
        <StatCard>
          <StatValue>{globalStats.totalUsers}</StatValue>
          <StatLabel>Total Utilisateurs</StatLabel>
          <StatExplanation>Nombre total de comptes créés</StatExplanation>
        </StatCard>
        <StatCard>
          <StatValue>{globalStats.activeUsers}</StatValue>
          <StatLabel>Utilisateurs Actifs</StatLabel>
          <StatExplanation>
            Connectés dans les {USER_ACTIVITY_THRESHOLDS.ACTIVE_DAYS} derniers
            jours
          </StatExplanation>
        </StatCard>
        <StatCard>
          <StatValue>{globalStats.retentionRate}%</StatValue>
          <StatLabel>Taux de Rétention</StatLabel>
          <StatExplanation>% d'utilisateurs qui restent actifs</StatExplanation>
        </StatCard>
        <StatCard>
          <StatValue>{globalStats.inactiveUsers}</StatValue>
          <StatLabel>Utilisateurs Inactifs</StatLabel>
          <StatExplanation>
            Pas connectés depuis {USER_ACTIVITY_THRESHOLDS.INACTIVE_DAYS}+ jours
          </StatExplanation>
        </StatCard>
      </StatsContainer>

      {/* Graphique des utilisateurs */}
      <ChartContainer>
        <ChartTitle>Évolution des Utilisateurs</ChartTitle>
        <ChartExplanation>
          <strong>📈 Évolution des Utilisateurs :</strong> Ce graphique montre
          l'évolution de votre communauté mois par mois.
          <br />• <strong>Nouveaux utilisateurs</strong> : Comptes créés ce
          mois-là
          <br />• <strong>Utilisateurs actifs</strong> : Ceux qui se sont
          connectés récemment (dans les {USER_ACTIVITY_THRESHOLDS.ACTIVE_DAYS}{" "}
          jours)
          <br />• <strong>Utilisateurs inactifs</strong> : Ceux qui ne se sont
          pas connectés depuis longtemps (90+ jours)
        </ChartExplanation>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="month"
              interval={0}
              tick={{ angle: -45, fontSize: 12 } as any}
              height={80}
            />
            <YAxis />
            <Tooltip
              formatter={(value: any, name: string) => [
                value,
                name === "new_users"
                  ? "Nouveaux"
                  : name === "active_users"
                  ? "Actifs"
                  : name === "inactive_users"
                  ? "Inactifs"
                  : name === "total_users"
                  ? "Total"
                  : name,
              ]}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="new_users"
              name="Nouveaux utilisateurs"
              stroke={CHART_COLORS.newUsers}
              strokeWidth={2}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="active_users"
              name="Utilisateurs actifs"
              stroke={CHART_COLORS.activeUsers}
              strokeWidth={2}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="inactive_users"
              name="Utilisateurs inactifs"
              stroke={CHART_COLORS.inactiveUsers}
              strokeWidth={2}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>

      {/* Graphique du taux de rétention */}
      <ChartContainer>
        <ChartTitle>Taux de Rétention par Mois</ChartTitle>
        <ChartExplanation>
          <strong>🎯 Taux de Rétention :</strong> Le taux de rétention mesure la
          capacité de votre jeu à garder ses joueurs actifs.
          <br />
          <strong>Formule :</strong> (Utilisateurs actifs / Total utilisateurs)
          × 100
          <br />• <strong>100%</strong> = Tous les utilisateurs de ce mois sont
          encore actifs (idéal)
          <br />• <strong>50%</strong> = La moitié des utilisateurs de ce mois
          sont encore actifs
          <br />• <strong>0%</strong> = Aucun utilisateur de ce mois n'est plus
          actif (problématique)
          <br />
          <em>
            Un bon taux de rétention indique que votre jeu plaît et que les
            joueurs reviennent !
          </em>
        </ChartExplanation>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="month"
              interval={0}
              tick={{ angle: -45, fontSize: 12 } as any}
              height={80}
            />
            <YAxis />
            <Tooltip
              formatter={(value: any) => [`${value}%`, "Taux de rétention"]}
            />
            <Bar
              dataKey="retention_rate"
              fill={CHART_COLORS.retentionRate}
              name="Taux de rétention (%)"
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};

export default EnhancedUsersChart;
